import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from "../../hooks/useSupabaseAuth"
import { API_ENDPOINTS, getAuthHeaders, shouldIncludeCredentials } from '../../lib/config/api-endpoints';

const ClientLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: checkingAuth } = useAuth();


  // Authentication is now handled by useAuth hook

  // Handle magic link token from URL
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      handleMagicLinkLogin(token);
    }
  }, [searchParams]);

  // If already authenticated, redirect to portal
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/client/portal', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleMagicLinkLogin = async (token: string) => {
    try {
      // Magic link verification endpoint
      const response = await fetch('/api/auth/magic-link/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ token }),
      });

      if (response.ok) {
        // Session cookie will be set by the API response
        // Check for redirect parameter
        const redirect = searchParams.get('redirect');
        if (redirect === 'content' || redirect === 'resources') {
          // Use window.location for full page reload to ensure cookies are set
          window.location.href = '/client/portal?tab=resources';
        } else {
          // Default redirect to portal
          window.location.href = '/client/portal';
        }
      } else {
        setError('Invalid or expired magic link');
      }
    } catch (error) {
      setError('Failed to verify magic link');
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Send magic link to the email address using Supabase auth endpoint
      const response = await fetch(API_ENDPOINTS.auth.magicLinkRequest, {
        method: 'POST',
        headers: getAuthHeaders(API_ENDPOINTS.auth.magicLinkRequest),
        credentials: shouldIncludeCredentials(API_ENDPOINTS.auth.magicLinkRequest) ? 'include' : 'same-origin',
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send magic link');
      }

      const data = await response.json();
      
      if (data.success) {
        // Handle local development auto-redirect
        const isLocalDev = import.meta.env.DEV || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        
        if (isLocalDev && data.magic_link) {
          console.log('🔧 LOCAL DEV: Auto-redirecting to magic link', data.magic_link);
          // Small delay to show the sending state, then redirect
          setTimeout(() => {
            window.location.href = data.magic_link;
          }, 500);
          return;
        }
        
        setMagicLinkSent(true);
        setError(null);
      } else {
        setError('Failed to send magic link. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send verification');
    } finally {
      setLoading(false);
    }
  };


  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-stone-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green-600 mx-auto mb-4"></div>
          <p className="text-brand-stone-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-stone-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green-600 mx-auto mb-4"></div>
          <p className="text-brand-stone-600">Redirecting to your portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-stone-100">
      <div className="bg-white shadow-2xl rounded-xl p-8 w-full max-w-md">
        <h1 className="text-3xl font-bold text-center text-brand-green-800 mb-6">
          Client Access
        </h1>
        
        {!magicLinkSent ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-brand-stone-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border border-brand-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green-500"
                placeholder="Enter your email address"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-green-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-brand-green-700 transition-colors duration-300 disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send Magic Link'}
            </button>

            {error && (
              <div className="text-red-600 text-sm text-center">
                {error}
              </div>
            )}

            <div className="mt-6 text-center text-sm text-brand-stone-500">
              <p>Enter the email address you used when filling out your intake form</p>
              <p className="mt-2">We'll send you a secure magic link to access your information</p>
            </div>
          </form>
        ) : (
          <div className="text-center">
            <div className="text-green-600 text-lg font-semibold mb-4">
              Magic Link Sent!
            </div>
            <div className="text-brand-stone-600 mb-6">
              <p>We've sent a secure link to <strong>{email}</strong></p>
              <p className="mt-2">Check your email and click the link to access your client portal</p>
            </div>
            <div className="text-sm text-brand-stone-500">
              <p>The link will expire in 1 hour for security</p>
              <p className="mt-2">
                Didn't receive the email? Check your spam folder or{' '}
                <button 
                  onClick={() => {
                    setMagicLinkSent(false);
                    setError(null);
                  }}
                  className="text-brand-green-600 hover:text-brand-green-700 underline"
                >
                  try again
                </button>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientLogin;